import { WorkAPI } from '../API'
import { collection } from '../../infra/collection'
import { JobTicketProps, JobTicket } from './JobTicket'
import { Id } from '../../types/definitions'
import {
  GetLatestOpenTicketsForJobsRequest,
  LatestOpenTicket,
} from '@taxfyle/api-internal/internal/ticket_pb'
import { timestampToISO } from '../../utils/grpc-util'
import { DateZero } from '../../utils/date-util'
import DataLoader from 'dataloader'
import { isNotNil } from '../../utils/object-util'

/**
 * Store for job tickets.
 */
export class JobTicketStore {
  private readonly ticketsLoader = new DataLoader(
    this._fetchLatestTicketsForJobs.bind(this),
    {
      cache: true,
      batch: true,
    },
  )

  /**
   * Tickets for jobs.
   */
  jobTickets = collection<JobTicketProps, JobTicket>({
    getDataId: (attrs) => `${attrs.job_id}/${attrs.id}`,
    getModelId: (model) => `${model.jobId}/${model.id}`,
    create: () => new JobTicket(),
  })

  /**
   * Constructor.
   */
  constructor(private readonly api: WorkAPI) {
    this.fetchLatestOpenTicketsForJobs =
      this.fetchLatestOpenTicketsForJobs.bind(this)
    this.getLatestTicketForJob = this.getLatestTicketForJob.bind(this)
  }

  /**
   * Returns the latest ticket for the given job, if any.
   *
   * @param jobId
   */
  getLatestTicketForJob(jobId: Id) {
    return this.jobTickets.find((t) => t.jobId === jobId) ?? null
  }

  /**
   * Fetches the latest open tickets for the given job IDs.
   * @param jobIds
   */
  async fetchLatestOpenTicketsForJobs(jobIds: readonly Id[]) {
    const ids = jobIds.filter(Id.some)
    const tickets = await Promise.all(
      ids.map((id) => this.ticketsLoader.load(id)),
    )
    return tickets.filter(isNotNil)
  }

  /**
   * Fetches the latest open tickets for the given job IDs, called by
   * the dataloader.
   *
   * @private
   * @param ids
   */
  private async _fetchLatestTicketsForJobs(ids: readonly string[]) {
    const request = new GetLatestOpenTicketsForJobsRequest()
    request.setJobIdsList(ids.slice())
    const response = await this.api.tickets.getLatestOpenTicketsForJobs(request)

    const responseMap = response.getLatestOpenTicketsMap()
    const mapped = Array.from(responseMap.entries()).map(
      ([jobId, latestOpenTicket]) => this._mapToProps(jobId, latestOpenTicket),
    )

    this.jobTickets.set(mapped)
    return ids.map(this.getLatestTicketForJob)
  }

  /**
   * Maps from the proto message to the props type.
   */
  private _mapToProps(
    jobId: string,
    latestOpenTicketDto: LatestOpenTicket,
  ): JobTicketProps {
    return {
      id: latestOpenTicketDto.getId(),
      job_id: jobId,
      date_updated:
        timestampToISO(latestOpenTicketDto.getUpdateTime()) ??
        DateZero().toISOString(),
      status_message: latestOpenTicketDto.getStatusMessage()?.getValue() ?? '',
    }
  }
}
